import { BackButton, ErrorAlert } from '@/components';
import { Button, PasswordInput, Text } from '@/components/atoms';
import { FormInput } from '@/components/molecules';
import EmailVerificationMessage from '@/components/organisms/Authentication/EmailVerificationMessage';
import { useAuth } from '@/components/providers/preauth/AuthProvider';
import { EMAIL_FORM_VALIDATIONS } from '@/utils/constants';
import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
interface Props {
  onBackClick: () => void;
  onForgotPasswordClick: () => void;
  onSignInClick: (email: string, password: string) => void;
  onSignUpClick: () => void;
  onGoogleSignInClick?: () => void;
  error?: string;
  initialEmail?: string;
}
type SignInFormFields = {
  email: string;
  password: string;
};
const SignInForm: FC<Props> = ({
  onBackClick,
  onForgotPasswordClick,
  onSignInClick,
  onSignUpClick,
  onGoogleSignInClick,
  error,
  initialEmail,
}) => {
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SignInFormFields>({
    defaultValues: {
      email: initialEmail || '',
      password: '',
    },
  });
  const { isEmailVerified } = useAuth();
  const [showVerification, setShowVerification] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  useEffect(() => {
    if (initialEmail) {
      setValue('email', initialEmail);
      setUserEmail(initialEmail);
    }
  }, [initialEmail, setValue]);
  useEffect(() => {
    if (error) {
      if (error.includes('verify your email')) {
        const email = watch('email') || initialEmail;
        if (email) {
          setUserEmail(email);
        }
        setShowVerification(true);
      } else {
        setError('email', { type: 'custom', message: error });
      }
    }
  }, [error, setError, watch, initialEmail]);
  const handleOnSubmit = handleSubmit(({ email, password }) => {
    setUserEmail(email);
    onSignInClick(email, password);
  });
  if (showVerification) {
    return (
      <EmailVerificationMessage
        email={userEmail}
        onBackToSignIn={() => setShowVerification(false)}
        onBackClick={onBackClick}
      />
    );
  }
  return (
    <div className="flex flex-col gap-20 w-full">
      <div className="flex flex-col gap-md">
        <BackButton onBackClick={onBackClick} />
      </div>
      <form onSubmit={handleOnSubmit} className="flex flex-col gap-md">
        <div className="flex flex-col gap-10">
          <Text value="Sign in" size="h1" />
          <div className="flex flex-col gap-10">
            <FormInput
              name="email"
              label="Email"
              type="email"
              register={register}
              rules={{
                required: 'Enter email to continue',
                validate: EMAIL_FORM_VALIDATIONS,
              }}
              errors={errors}
            />
            <div className="flex flex-col gap-2">
              <PasswordInput
                name="password"
                label="Password"
                type="password"
                register={register}
                rules={{
                  required: 'Enter password to continue',
                }}
                errors={errors}
                showError={false}
              />
              <Text
                value="Forgot password?"
                intent="link"
                color="accent"
                onClick={onForgotPasswordClick}
                className="mt-2"
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-sm">
          <Button type="submit" fullWidth>
            Sign in
          </Button>
          <div className="flex gap-2 items-baseline">
            <Text value="Don't have an account?" color="tertiary" />
            <Text
              value="Sign up"
              intent="link"
              color="accent"
              onClick={onSignUpClick}
            />
          </div>
        </div>
        {error && !error.includes('verify your email') && !errors.email && (
          <ErrorAlert title="Authentication Error" description={error} />
        )}
      </form>
    </div>
  );
};
export default SignInForm;
